import React, { useState, useEffect, useCallback } from 'react';
import { Thermometer, AlertTriangle, Check, Loader2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

/**
 * Shows today's temperature-log coverage per store.
 * SM: their own store only. OM/MD: every active store.
 * Click → /admin/temperature-logs (full history + filter).
 */
export default function TempLogsTodayWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ stores: [], total_slots_per_day: 6 });

  const load = useCallback(async () => {
    try {
      const r = await apiRequest('/temperature-logs/coverage/today');
      setData(r);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(i);
  }, [load]);

  const stores = data.stores || [];
  const totalAlerts = stores.reduce((a, s) => a + (s.alerts_today || 0), 0);
  const totalMissed = stores.reduce((a, s) => a + (s.missed || 0), 0);

  return (
    <Card className="h-full" data-testid="temp-logs-today-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Thermometer className="h-4 w-4 text-blue-600" />
          Temperature Logs — Today
          {totalAlerts > 0 && (
            <Badge variant="destructive" className="ml-auto h-5 px-1.5 text-xs">
              {totalAlerts} alert{totalAlerts !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : stores.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No store data.</p>
        ) : (
          <>
            {totalMissed > 0 && (
              <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mb-1">
                <AlertTriangle className="h-3 w-3" />
                {totalMissed} slot{totalMissed !== 1 ? 's' : ''} missed across {stores.filter(s => s.missed).length} store{stores.filter(s => s.missed).length !== 1 ? 's' : ''}
              </div>
            )}
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
              {stores.map(s => {
                const pct = s.expected > 0 ? Math.round((s.completed / s.expected) * 100) : 100;
                const tone = s.alerts_today > 0
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : s.missed > 0
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
                return (
                  <div key={s.store}
                       className={`flex items-center justify-between border rounded-md px-2.5 py-1.5 ${tone}`}
                       data-testid={`temp-store-row-${s.store}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.store}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.completed}/{s.expected} expected · {s.alerts_today || 0} alert{s.alerts_today === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {s.alerts_today > 0 ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : pct === 100 ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : null}
                      <span className={`text-xs font-semibold tabular-nums ${
                        pct === 100 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'
                      }`}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="ghost" size="sm"
              className="w-full justify-between mt-1 text-blue-600"
              onClick={() => navigate('/admin/temperature-logs')}
              data-testid="temp-logs-open-full-btn"
            >
              View all logs
              <ChevronRight className="h-3 w-3" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
